import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useDeleteSessionPermanently } from "@/hooks/api/mutate/useDeleteSessionPermanently";
import { useUnarchiveSession } from "@/hooks/api/mutate/useUnarchiveSession";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  toPortfolioCardData,
  type PortfolioCardData,
} from "@/utils/portfolioCardData";
import {
  DEFAULT_PORTFOLIO_PREFS,
  loadPortfolioPrefs,
  savePortfolioPrefs,
  type PortfolioFilter,
  type PortfolioSort,
  type PortfolioView,
} from "@/utils/portfolioPrefs";
import { CompanyCard } from "./components/CompanyCard";
import { DeleteCompanyDialog } from "./components/DeleteCompanyDialog";
import { EmptyState } from "./components/EmptyState";
import { PortfolioToolbar } from "./components/PortfolioToolbar";
import { NotificareRecalcul } from "@/components/notificari/NotificareRecalcul";

function applyFilter(
  cards: PortfolioCardData[],
  filter: PortfolioFilter
): PortfolioCardData[] {
  switch (filter) {
    case "active":
      return cards.filter((card) => !card.isArchived);
    case "arhivate":
      return cards.filter((card) => card.isArchived);
    case "cu-alerte":
    case "toate":
    default:
      return cards;
  }
}

function applySearch(
  cards: PortfolioCardData[],
  search: string
): PortfolioCardData[] {
  const term = search.trim().toLowerCase();
  if (!term) return cards;
  return cards.filter((card) => {
    const name = card.displayName.toLowerCase();
    const cui = card.cui?.toLowerCase() ?? "";
    return name.includes(term) || cui.includes(term);
  });
}

function applySort(
  cards: PortfolioCardData[],
  sort: PortfolioSort
): PortfolioCardData[] {
  const sorted = [...cards];
  if (sort === "nume-az") {
    sorted.sort((a, b) =>
      a.displayName.localeCompare(b.displayName, "ro", { sensitivity: "base" })
    );
  } else {
    sorted.sort((a, b) => {
      const aTime = a.session.updated_at
        ? new Date(a.session.updated_at).getTime()
        : 0;
      const bTime = b.session.updated_at
        ? new Date(b.session.updated_at).getTime()
        : 0;
      return bTime - aTime;
    });
  }
  return sorted;
}

function CompaniesPortfolio() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    sessions,
    archivedSessions,
    isLoadingSessions,
    createSession,
    switchToSession,
    archiveSessionById,
    refreshSessions,
  } = useSessionSwitcher();

  const { mutateAsync: unarchiveAsync, isPending: isUnarchiving } =
    useUnarchiveSession();
  const { mutateAsync: deleteAsync, isPending: isDeleting } =
    useDeleteSessionPermanently();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PortfolioCardData | null>(
    null
  );

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<PortfolioFilter>(
    DEFAULT_PORTFOLIO_PREFS.filter
  );
  const [sort, setSort] = useState<PortfolioSort>(DEFAULT_PORTFOLIO_PREFS.sort);
  const [view, setView] = useState<PortfolioView>(DEFAULT_PORTFOLIO_PREFS.view);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  useEffect(() => {
    const prefs = loadPortfolioPrefs();
    setFilter(prefs.filter);
    setSort(prefs.sort);
    setView(prefs.view);
    setPrefsLoaded(true);
  }, []);

  useEffect(() => {
    if (!prefsLoaded) return;
    savePortfolioPrefs({ filter, sort, view });
  }, [filter, sort, view, prefsLoaded]);

  const allCards = useMemo(() => {
    const all = [...sessions, ...archivedSessions];
    return all.map((session, index) => toPortfolioCardData(session, index));
  }, [sessions, archivedSessions]);

  const visibleCards = useMemo(() => {
    return applySort(applySearch(applyFilter(allCards, filter), search), sort);
  }, [allCards, filter, search, sort]);

  const handleAddCompany = async () => {
    const success = await createSession();
    if (success) {
      navigate("/identificare-raportare");
    }
  };

  const handleOpen = async (sessionId: string, isArchived: boolean) => {
    setBusyId(sessionId);
    try {
      if (isArchived) {
        await unarchiveAsync(sessionId);
        await refreshSessions();
      }
      await switchToSession(sessionId);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error opening session from portfolio:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut deschide compania.",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleArchive = async (sessionId: string) => {
    setBusyId(sessionId);
    try {
      await archiveSessionById(sessionId);
    } finally {
      setBusyId(null);
    }
  };

  const handleUnarchive = async (sessionId: string) => {
    setBusyId(sessionId);
    try {
      await unarchiveAsync(sessionId);
      await refreshSessions();
    } catch (error) {
      console.error("Error unarchiving session from portfolio:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut dezarhiva compania.",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setBusyId(targetId);
    try {
      await deleteAsync(targetId);
      await refreshSessions();
      setDeleteTarget(null);
      toast({
        title: "Companie ștearsă",
        description: "Compania și toate datele asociate au fost șterse.",
      });
    } catch (error) {
      console.error("Error deleting session from portfolio:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut șterge compania.",
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const activeCount = sessions.length;
  const hasNoSessions = allCards.length === 0;
  const hasNoMatches = !hasNoSessions && visibleCards.length === 0;
  const gridClasses =
    view === "list"
      ? "grid grid-cols-1 gap-3"
      : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3";

  const handleEditCompany = (sessionId: string) => {
    void switchToSession(sessionId).then((ok) => {
      if (ok) navigate("/identificare-raportare");
    });
  };

  return (
    <div className="min-h-screen bg-ds-surface-50">
      <main className="p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <header className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-ds-blue-50 text-ds-blue-600 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <h1
                  data-testid="portfolio-page-title"
                  className="font-heading text-2xl md:text-3xl font-bold text-navy-900"
                >
                  Companiile mele
                </h1>
                <p
                  data-testid="portfolio-page-subtitle"
                  className="text-sm md:text-base text-ds-text-500 mt-1"
                >
                  Gestionează conformitatea NIS2 pentru{" "}
                  <span data-testid="portfolio-active-count">
                    {activeCount}
                  </span>{" "}
                  {activeCount === 1 ? "companie" : "companii"}
                </p>
              </div>
            </div>

            <Button
              onClick={handleAddCompany}
              disabled={isLoadingSessions}
              data-testid="portfolio-add-company"
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Adaugă companie
            </Button>
          </header>

          <NotificareRecalcul />

          {isLoadingSessions ? (
            <div className="text-center py-16 text-ds-text-500">
              Se încarcă companiile...
            </div>
          ) : (
            <>
              <PortfolioToolbar
                search={search}
                onSearchChange={setSearch}
                filter={filter}
                onFilterChange={setFilter}
                sort={sort}
                onSortChange={setSort}
                view={view}
                onViewChange={setView}
              />

              {hasNoSessions || hasNoMatches ? (
                <EmptyState
                  variant={hasNoSessions ? "no-sessions" : "no-matches"}
                  onAddCompany={handleAddCompany}
                  isBusy={isLoadingSessions}
                />
              ) : (
                <div
                  data-testid="portfolio-card-grid"
                  data-view={view}
                  className={cn(gridClasses)}
                >
                  {visibleCards.map((card) => (
                    <CompanyCard
                      key={card.id}
                      card={card}
                      onOpen={() => handleOpen(card.id, card.isArchived)}
                      onEdit={() => handleEditCompany(card.id)}
                      onArchive={() => handleArchive(card.id)}
                      onUnarchive={() => handleUnarchive(card.id)}
                      onDelete={() => setDeleteTarget(card)}
                      isBusy={busyId === card.id || isUnarchiving}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <DeleteCompanyDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        companyName={deleteTarget?.displayName ?? ""}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

export default CompaniesPortfolio;
